import React, { useState, useEffect } from 'react';
import { Container, Grid, TextField, Select, MenuItem, InputLabel, FormControl, Checkbox, FormControlLabel, CircularProgress } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { LeadIndustry, LeadRating, LeadSource, LeadStatus } from '../../../utils/enums';
import AccountsAutoComplete from '../../Accounts/AccountsAutoComplete';

const EditForm = ({ leadData, onSubmit, leadOwnerOptions, loading }) => {
    const [focusedField, setFocusedField] = useState(null); // Track the focused field

    const [ownerId, setOwnerId] = useState('');
    const [company, setCompany] = useState('');
    const [accountId, setAccountId] = useState(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [title, setTitle] = useState('');
    const [fax, setFax] = useState('');
    const [phone, setPhone] = useState('');
    const [otherPhone, setOtherPhone] = useState(null);
    const [website, setWebsite] = useState('');
    const [mobile, setMobile] = useState('');
    const [leadStatus, setLeadStatus] = useState(0);
    const [leadSource, setLeadSource] = useState(0);
    const [numberOfEmployees, setNumberOfEmployees] = useState(0);
    const [leadIndustry, setLeadIndustry] = useState(0);
    const [skypeID, setSkypeID] = useState('');
    const [annualRevenue, setAnnualRevenue] = useState(0);
    const [leadRating, setLeadRating] = useState(0);
    const [emailOptOut, setEmailOptOut] = useState(true);
    const [secondaryEmail, setSecondaryEmail] = useState('');
    const [twitter, setTwitter] = useState('');
    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [country, setCountry] = useState('');
    const [description, setDescription] = useState('');
    const [shouldFetchSingleAccount, setShouldFetchSingleAccount] = useState(false);


    // Update state values when leadData changes
    useEffect(() => {
        if (leadData) {
            setOwnerId(leadData.ownerId || '');
            setCompany(leadData.company || '');
            setAccountId(leadData.accountId || null);
            setShouldFetchSingleAccount(true);
            setFirstName(leadData.firstName || '');
            setLastName(leadData.lastName || '');
            setLeadSource(leadData.leadSource || 0);
            setLeadStatus(leadData.leadStatus || 0);
            setLeadIndustry(leadData.leadIndustry || 0);
            setLeadRating(leadData.leadRating || 0);
            setEmail(leadData.email || '');
            setTitle(leadData.title || '');
            setFax(leadData.fax || '');
            setPhone(leadData.phone || '');
            setOtherPhone(leadData.otherPhone || '')
            setWebsite(leadData.website || '');
            setMobile(leadData.mobile || '');
            setSkypeID(leadData.skypeID || '');
            setAnnualRevenue(leadData.annualRevenue || 0);
            setNumberOfEmployees(leadData.numberOfEmployees || 0);
            setEmailOptOut(leadData.emailOptOut ?? true);  // Handles boolean case
            setSecondaryEmail(leadData.secondaryEmail || '');
            setTwitter(leadData.twitter || '');
            setStreet(leadData.street || '');
            setCity(leadData.city || '');
            setState(leadData.state || '');
            setZipCode(leadData.zipCode || '');
            setCountry(leadData.country || '');
            setDescription(leadData.description || '');
        }
    }, [leadData, leadOwnerOptions]);

    const handleSubmit = (e) => {
        e.preventDefault();

        // Collect all form data
        const formData = {
            ownerId,
            company,
            accountId,
            firstName,
            lastName,
            email,
            title,
            fax,
            phone,
            otherPhone,
            website,
            mobile,
            leadStatus,
            leadSource,
            numberOfEmployees,
            leadIndustry,
            skypeID,
            annualRevenue,
            leadRating,
            emailOptOut,
            secondaryEmail,
            twitter,
            street,
            city,
            state,
            zipCode,
            country,
            description,
            "isContact": true
        };
        // Call onSubmit function passed as a prop
        onSubmit(formData);
    };
    if (!leadData) {
        return (
            <>
                Loading...
            </>
        )
    }
    return (
        <form id='create-form' onSubmit={handleSubmit}>
            <Container maxWidth="md" sx={{ marginTop: "10px" }}>
                <h4>Lead Information</h4>
                <Grid container spacing={3}>
                    {/* Lead Owner */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            required
                            disablePortal
                            id="lead-owner-autocomplete"
                            options={leadOwnerOptions}
                            loading={loading}
                            loadingText="Loading..."
                            value={leadOwnerOptions.find(option => option.value === leadData.ownerId) || null}
                            onFocus={() => setFocusedField('leadOwner')}
                            onBlur={() => setFocusedField(null)}
                            onChange={(event, newValue) => setOwnerId(newValue ? newValue.value : '')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Contact Owner"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {focusedField === 'leadOwner' && loading && (
                                                    <CircularProgress color="inherit" size={20} />
                                                )}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    {/* Company */}
                    <Grid item xs={12} sm={6}>
                        {/* <TextField fullWidth label="Company" required
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                            InputLabelProps={{ shrink: !!company }} /> */}
                        <AccountsAutoComplete accountId={accountId} setAccountId={setAccountId} shouldFetchSingleAccount={shouldFetchSingleAccount}/>
                    </Grid>

                    {/* First Name */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="First Name" required
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            InputLabelProps={{ shrink: !!firstName }} />
                    </Grid>

                    {/* Last Name */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Last Name" required
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            InputLabelProps={{ shrink: !!lastName }} />
                    </Grid>

                    {/* Email */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            InputLabelProps={{ shrink: !!email }} />
                    </Grid>

                    {/* Title */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            InputLabelProps={{ shrink: !!title }} />
                    </Grid>

                    {/* Fax */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Fax"
                            value={fax}
                            onChange={(e) => setFax(e.target.value)}
                            InputLabelProps={{ shrink: !!fax }} />
                    </Grid>

                     {/* Website */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Website"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                            InputLabelProps={{ shrink: !!website }} />
                    </Grid>

                    {/* Phone */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Phone"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            InputLabelProps={{ shrink: !!phone }} />
                    </Grid>

                    {/* Other Phone */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Other Phone"
                            value={otherPhone}
                            onChange={(e) => setOtherPhone(e.target.value)}
                            InputLabelProps={{ shrink: !!otherPhone }} />
                    </Grid>

                    {/* Mobile */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Mobile"
                            value={mobile}
                            onChange={(e) => setMobile(e.target.value)}
                            InputLabelProps={{ shrink: !!mobile }} />
                    </Grid>

                    {/* Lead Status */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            disablePortal
                            id="combo-box-demo"
                            options={LeadStatus}
                            sx={{ width: "100%" }}
                            value={LeadStatus.find((option) => option.value === leadData.leadStatus)}
                            onChange={(event, newValue) => setLeadStatus(newValue ? newValue.value : '')}
                            renderInput={(params) => <TextField {...params} label="Lead Status" />}
                        />
                    </Grid>

                    {/* Lead Source */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            disablePortal
                            id="combo-box-demo"
                            options={LeadSource}
                            sx={{ width: "100%" }}
                            value={LeadSource.find((option) => option.value === leadData.leadSource)}
                            onChange={(event, newValue) => setLeadSource(newValue ? newValue.value : '')}
                            renderInput={(params) => <TextField {...params} label="Lead Source" />}
                        />
                    </Grid>

                    {/* No. of Employees */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="No. of Employees"
                            value={numberOfEmployees}
                            onChange={(e) => setNumberOfEmployees(e.target.value)}
                             />
                    </Grid>

                    {/* Industry */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            disablePortal
                            id="combo-box-demo"
                            options={LeadIndustry}
                            sx={{ width: "100%" }}
                            value={LeadIndustry.find((option) => option.value === leadData.leadIndustry)}
                            onChange={(event, newValue) => setLeadIndustry(newValue ? newValue.value : '')}
                            renderInput={(params) => <TextField {...params} label="Industry" />}
                        />
                    </Grid>

                    {/* Skype ID */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Skype ID"
                            value={skypeID}
                            onChange={(e) => setSkypeID(e.target.value)}
                            InputLabelProps={{ shrink: !!skypeID }} />
                    </Grid>

                    {/* Annual Revenue */}
                    <Grid item xs={12} sm={6}>
                        <TextField shrink fullWidth label="Annual Revenue" InputProps={{ startAdornment: <span>$</span> }}
                            value={annualRevenue}
                            onChange={(e) => setAnnualRevenue(e.target.value)}
                             />
                    </Grid>

                    {/* Rating */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            disablePortal
                            id="combo-box-demo"
                            options={LeadRating}
                            sx={{ width: "100%" }}
                            value={LeadRating.find((option) => option.value === leadData.leadRating)}
                            onChange={(event, newValue) => setLeadRating(newValue ? newValue.value : '')}
                            renderInput={(params) => <TextField {...params} label="Rating" />}
                        />
                    </Grid>

                    {/* Email Opt Out */}
                    <Grid item xs={12} sm={6}>
                    <FormControlLabel control={<Checkbox checked={emailOptOut} onChange={(e) => {
                            setEmailOptOut(e.target.checked);
                        }   } />} label="Email Opt Out" />
                    </Grid>

                    {/* Secondary Email */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Secondary Email"
                            value={secondaryEmail}
                            onChange={(e) => setSecondaryEmail(e.target.value)}
                            InputLabelProps={{ shrink: !!secondaryEmail }} />
                    </Grid>

                    {/* Twitter */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Twitter" InputProps={{ startAdornment: <span>@</span> }}
                            value={twitter}
                            onChange={(e) => setTwitter(e.target.value)} />
                    </Grid>
                </Grid >
                <br />
                <h4>Address Information</h4>
                <Grid container spacing={3}>
                    {/* Street */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Street"
                            value={street}
                            onChange={(e) => setStreet(e.target.value)}
                            InputLabelProps={{ shrink: !!street }} />
                    </Grid>

                    {/* City */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="City"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            InputLabelProps={{ shrink: !!city }} />
                    </Grid>

                    {/* State */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="State"
                            value={state}
                            onChange={(e) => setState(e.target.value)}
                            InputLabelProps={{ shrink: !!state }} />
                    </Grid>

                    {/* Zip Code */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Zip Code"
                            value={zipCode}
                            onChange={(e) => setZipCode(e.target.value)}
                            InputLabelProps={{ shrink: !!zipCode }} />
                    </Grid>

                    {/* Country */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Country"
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                            InputLabelProps={{ shrink: !!country }} />
                    </Grid>
                </Grid >
                <br />
                <h4>Description Information</h4>
                <Grid container spacing={3}>
                    {/* Description */}
                    <Grid item xs={12} sm={12}>
                        <TextField multiline fullWidth label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            InputLabelProps={{ shrink: !!description }} />
                    </Grid>
                </Grid >
            </Container >
        </form>
    );
};

export default EditForm;
